import SubtleBadge from "components/common/SubtleBadge";
import React, { useState } from "react";
import { Card, Image, Button, Spinner, Form } from "react-bootstrap";

export default function SecurityPhotoSection({
  photoUrl,
  verified,
  verificationDecision,
  verifiedOn,
  uploadedOn,
  uploadedBy,
  handlePreviewClick,
  onVerify,
  verifying,
  isSameUser,
}) {
  const [selectedDecision, setSelectedDecision] = useState("APPROVED");
  const [showDecisionDropdown, setShowDecisionDropdown] = useState(false);

  if (!photoUrl) {
    return (
      <Card className="custom-shadow border-0 mb-3">
        <Card.Body>
          <h5 className="fw-semibold mb-2 text-sm">Security Photo</h5>
          <p className="text-muted mb-0 text-xs">
            No security photo available.
          </p>
        </Card.Body>
      </Card>
    );
  }

  // Helper to format date
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleString();
    } catch {
      return "N/A";
    }
  };

  // Get status text based on verification decision
  const getStatusText = () => {
    if (!verified) return "Pending Review";
    if (verificationDecision === "APPROVED") return "Approved";
    if (verificationDecision === "REJECTED") return "Rejected";
    return "Verified";
  };

  // Get badge color based on verification decision
  const getBadgeColor = () => {
    if (!verified) return "warning";
    if (verificationDecision === "APPROVED") return "success";
    if (verificationDecision === "REJECTED") return "danger";
    return "success";
  };

  // Handle verification with decision
  const handleVerifyWithDecision = () => {
    onVerify(selectedDecision);
  };

  return (
    <div className="shadow-smdd border-0 mb-4">
      <Card.Header className="bg-white d-flex align-items-center justify-content-between">
        <h5 className="fw-semibold mb-0 text-sm">Security Photo</h5>
      </Card.Header>

      <Card.Body className="d-flex align-items-centerdd gap-4">
        <div
          className="border rounded p-2 bg-light mt-2"
          style={{
            width: 180,
            height: 150,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
          onClick={() => handlePreviewClick(photoUrl)}
        >
          <Image
            src={photoUrl}
            alt="Security selfie"
            fluid
            style={{ maxHeight: "100%", objectFit: "contain" }}
            draggable={false}
          />
        </div>

        <div>
          <p className="mb-1 fw-semibold">Live Capture / Selfie</p>
          <small className="text-muted">
            This photo is used to compare the user's identity against the
            profile photo and submitted ID documents.
          </small>
          <small className="d-block mt-2 mb-3">
            (click image to view in full mode)
          </small>
          
          {/* Verification Status */}
          <div className="mb-2">
            <small>Status: </small>
            <SubtleBadge bg={getBadgeColor()}>
              {getStatusText()}
            </SubtleBadge>
          </div>
          
          {/* Verification Decision Details - show always when photo exists */}
          {photoUrl && (
            <>
              <div className="mb-1">
                <small className="text-muted">Decision: </small>
                <small className="fw-semibold">
                  {verificationDecision === 'REJECTED' ? 'Rejected' : (verificationDecision || (verified ? 'Approved' : 'Pending'))}
                </small>
              </div>
              <div className="mb-1">
                <small className="text-muted">Photo Captured: </small>
                <small>{formatDate(uploadedOn)}</small>
              </div>
              {verifiedOn && (
                <div className="mb-1">
                  <small className="text-muted">{verificationDecision === 'REJECTED' ? 'Rejected On: ' : 'Verified On: '}</small>
                  <small>{formatDate(verifiedOn)}</small>
                </div>
              )}
            </>
          )}
          
          {/* Verification Actions */}
          {photoUrl && !isSameUser && (
            <div className="mt-3">
              {!verified ? (
                // Show decision dropdown and verify button
                <>
                  <div className="mb-2">
                    <Form.Select
                      size="sm"
                      value={selectedDecision}
                      onChange={(e) => setSelectedDecision(e.target.value)}
                      style={{ width: '200px' }}
                    >
                      <option value="APPROVED">Approve</option>
                      <option value="REJECTED">Reject</option>
                    </Form.Select>
                    {selectedDecision === "REJECTED" && (
                      <small className="text-danger d-block mt-1">
                        User will be asked to upload a new photo
                      </small>
                    )}
                  </div>
                  <Button
                    variant={selectedDecision === "APPROVED" ? "success" : "danger"}
                    size="sm"
                    onClick={handleVerifyWithDecision}
                    disabled={verifying}
                  >
                    {verifying ? (
                      <>
                        <Spinner animation="border" size="sm" /> Processing...
                      </>
                    ) : selectedDecision === "APPROVED" ? (
                      "Approve Photo"
                    ) : (
                      "Reject Photo"
                    )}
                  </Button>
                </>
              ) : (
                // Already verified - show status only (details already shown above)
                <div className="d-flex align-items-center gap-2">
                  <SubtleBadge bg={getBadgeColor()}>
                    {getStatusText()}
                  </SubtleBadge>
                </div>
              )}
            </div>
          )}
        </div>
      </Card.Body>
    </div>
  );
}
